/**
 * The pointer on the columns: hover moves the active column, a press and drag makes a span, a press
 * alone picks one, and a press on a chosen span's handle drags that end (round 2, dataviz S1). After
 * a pick the tooltip stays away until the pointer moves again, so it never covers what was chosen.
 */
import { useState, type PointerEvent } from 'react';
import { indexAt, spanEdges, type Frame } from './columns-geometry';

export interface ColumnSpan {
  from: number;
  to: number;
}

export interface PointerTargets {
  selected?: ColumnSpan | null | undefined;
  onSelect?: ((index: number) => void) | undefined;
  onSpan?: ((from: number, to: number) => void) | undefined;
}

/** How near a handle, in the frame's pixels, a press grabs it. */
const GRAB = 8;

export const ordered = (s: ColumnSpan): ColumnSpan => ({
  from: Math.min(s.from, s.to),
  to: Math.max(s.from, s.to),
});

/** A press on a span's handle: the drag anchored at the other end, or null when no handle is near. */
export function grabbed(
  f: Frame,
  span: ColumnSpan | null | undefined,
  x: number,
): ColumnSpan | null {
  if (!span) return null;
  const [left, right] = spanEdges(f, span.from, span.to);
  const toLeft = Math.abs(x - left);
  const toRight = Math.abs(x - right);
  if (Math.min(toLeft, toRight) > GRAB) return null;
  return toLeft < toRight ? { from: span.to, to: span.from } : { from: span.from, to: span.to };
}

/** A press released: a span when it covers several columns, else the one column. */
function finish(drag: ColumnSpan, targets: PointerTargets): void {
  const { from, to } = ordered(drag);
  if (from !== to && targets.onSpan) targets.onSpan(from, to);
  else targets.onSelect?.(from);
}

/** The pointer's x in the frame's own pixels. */
function frameX(f: Frame, e: PointerEvent<SVGSVGElement>): number {
  const box = e.currentTarget.getBoundingClientRect();
  return ((e.clientX - box.left) / box.width) * f.width;
}

export function useColumnsPointer(
  f: Frame,
  count: number,
  targets: PointerTargets,
  setActive: (i: number | null) => void,
) {
  const [drag, setDrag] = useState<ColumnSpan | null>(null);
  const [quiet, setQuiet] = useState(false);
  const xAt = (e: PointerEvent<SVGSVGElement>) => frameX(f, e);
  const pickable = targets.onSelect !== undefined || targets.onSpan !== undefined;
  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (!pickable) return;
    e.preventDefault(); // a mouse press must not focus the chart: the focus ring is for the keyboard
    const x = xAt(e);
    const i = indexAt(f, x, count);
    const handle = targets.onSpan ? grabbed(f, targets.selected, x) : null;
    setDrag(handle ?? { from: i, to: i });
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const i = indexAt(f, xAt(e), count);
    setActive(i);
    if (drag) setDrag({ from: drag.from, to: i });
    else setQuiet(false);
  };
  const onPointerUp = () => {
    if (drag) {
      finish(drag, targets);
      setQuiet(true);
    }
    setDrag(null);
  };
  return {
    drag,
    quiet,
    /** The keyboard moved: the tooltip may come back. */
    wake: () => setQuiet(false),
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerLeave: () => setActive(null) },
  };
}
